import { guestApiClient } from './apiClient';
import { ApiResponse } from '../types';

export type HotelInfoSectionGroup = 'essentials' | 'stay' | 'offers' | 'other';

export interface HotelInfoSection {
  slug: string;
  title: string;
  body: string;
  icon: string;
  language: string;
  // Optional defensively — older cached responses or a future backend bug
  // could omit it. HotelInfoScreen treats a missing group as 'stay'.
  group?: HotelInfoSectionGroup;
}

export interface HotelInfoData {
  hotel: {
    name: string;
    code: string;
    phone: string;
    email: string;
    // Richer hotel profile fields — all optional/nullable, render conditionally.
    description?: string | null;
    website?: string | null;
    street_address?: string | null;
    city?: string | null;
    country?: string | null;
    check_in_time?: string | null;
    check_out_time?: string | null;
    amenities?: string[];
    // Per-hotel opt-in (Settings → Guest Book), false/absent = hide the
    // Order tab and food ordering entirely.
    food_ordering_enabled?: boolean;
    // Relative path (e.g. `/api/guest/hotel-logo/CODE/`) — prefix with the
    // API base via `hotelLogoSrc` before rendering, never used as-is.
    logo_url?: string | null;
    // Per-hotel guest-app config (admin "Guest App" page). All optional —
    // absent fields mean the defaults (everything on, phone contact).
    features?: {
      self_checkin?: boolean;
      payments?: boolean;
      housekeeping?: boolean;
      food_ordering?: boolean;
      housekeeping_when?: boolean;
    };
    help_contact?: {
      mode?: 'phone' | 'whatsapp' | 'hidden';
      phone?: string | null;
      whatsapp?: string | null;
    };
    brand_color?: string;
    header_color?: string;
    welcome_text?: string;
    button_labels?: Record<string, string>;
    checkin_payment_policy?: string;
    checkin_kiosk_pay_at_reception?: boolean;
    currency?: string;
  };
  language: string;
  available_languages: string[];
  sections: HotelInfoSection[];
}

class HotelInfoService {
  /**
   * Get the digital guest-book content for a hotel. Public endpoint — no auth.
   * Per-section English fallback happens server-side; a section's own
   * `language` field may differ from the requested `lang`.
   */
  async getHotelInfo(hotelCode: string, lang: string): Promise<ApiResponse<HotelInfoData>> {
    const response = await guestApiClient.get<HotelInfoData>(
      `/api/guest/hotel-info/?hotel_code=${encodeURIComponent(hotelCode)}&lang=${encodeURIComponent(lang)}`
    );

    // Backend wraps the payload in its own {success, data} envelope on top
    // of the axios/apiClient envelope — unwrap defensively like the other
    // guest services do, in case that ever changes.
    const body: any = response.data;
    if (body && typeof body === 'object' && 'data' in body && body.data) {
      return {
        success: true,
        data: body.data as HotelInfoData,
      };
    }

    return response;
  }
}

export const hotelInfoService = new HotelInfoService();

/**
 * Resolve a hotel's logo to an absolute, renderable URL, or null when the
 * hotel has none. `hotel.logo_url` is a relative path (e.g.
 * `/api/guest/hotel-logo/CODE/`) — prefix it with the app's API base, same
 * pattern used for other guest-facing images (see DocumentViewer.tsx).
 */
export function hotelLogoSrc(
  hotel?: Pick<HotelInfoData['hotel'], 'logo_url'> | null
): string | null {
  if (!hotel?.logo_url) return null;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${apiBase}${hotel.logo_url}`;
}
